import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../api/endpoints';
import type { NotificationChannel, RouteSettings } from '../api/types';
import { useAuth } from '../auth/context';
import { ErrorMessage } from '../components/ErrorMessage';
import { PageLoader } from '../components/Loader';
import { MutationError } from '../components/MutationError';

const channelLabels: Record<NotificationChannel, string> = { IN_APP: 'Intern', EMAIL: 'E-Mail', BOTH: 'Beides', NONE: 'Aus' };

// NotificationSettings: Benachrichtigungs-E-Mail und Kanal je Ereignis (intern, E-Mail, beides, aus).
export function NotificationSettings() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['me', 'notification-settings'], queryFn: ({ signal }) => api.notificationSettings(signal) });
  const [email, setEmail] = useState<string>();
  const [channels, setChannels] = useState<Record<string, NotificationChannel>>({});
  const save = useMutation({
    mutationFn: () =>
      api.saveNotificationSettings({
        notification_email: email === undefined ? undefined : email.trim() || null,
        preferences: Object.entries(channels).map(([type, channel]) => ({ type, channel })),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['me', 'notification-settings'], data);
      setChannels({});
      setEmail(undefined);
    },
  });
  return (
    <section className="card" id="benachrichtigungen" aria-labelledby="notify-title">
      <h2 id="notify-title">Benachrichtigungen</h2>
      {settings.isPending ? (
        <PageLoader />
      ) : settings.isError ? (
        <ErrorMessage error={settings.error} />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <label className="field">
            <span>E-Mail für Benachrichtigungen (leer = {settings.data.account_email})</span>
            <input type="email" value={email ?? settings.data.notification_email ?? ''} onChange={(e) => setEmail(e.target.value)} placeholder={settings.data.account_email} />
          </label>
          <div className="table-wrap">
            <table className="table compact">
              <thead>
                <tr>
                  <th scope="col">Ereignis</th>
                  <th scope="col">Kanal</th>
                </tr>
              </thead>
              <tbody>
                {settings.data.preferences.map((p) => (
                  <tr key={p.type}>
                    <td>{p.label}</td>
                    <td>
                      <label className="visually-hidden" htmlFor={`channel-${p.type}`}>
                        Kanal für {p.label}
                      </label>
                      <select id={`channel-${p.type}`} value={channels[p.type] ?? p.channel} onChange={(e) => setChannels({ ...channels, [p.type]: e.target.value as NotificationChannel })}>
                        {(Object.keys(channelLabels) as NotificationChannel[])
                          .filter((c) => p.email_capable || (c !== 'EMAIL' && c !== 'BOTH'))
                          .map((c) => (
                            <option key={c} value={c}>
                              {channelLabels[c]}
                              {c === p.default_channel ? ' (Standard)' : ''}
                            </option>
                          ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {save.isError && <MutationError error={save.error} />}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              Speichern
            </button>
            {save.isSuccess && <span className="muted">Gespeichert</span>}
          </div>
        </form>
      )}
    </section>
  );
}

const startLabels: Record<RouteSettings['start_type'], string> = {
  FIRST_APPOINTMENT: 'Erster Termin (keine Anfahrt berechnen)',
  OFFICE: 'Büro (Projekteinstellung)',
  HOME: 'Wohnort',
  CUSTOM: 'Individuelle Startadresse',
};

// RouteStartSettings: Startpunkt der Tagesroute. Wohnort/Adresse sieht nur der Benutzer selbst.
export function RouteStartSettings() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['me', 'route-settings'], queryFn: ({ signal }) => api.routeSettings(signal) });
  const [draft, setDraft] = useState<RouteSettings>();
  const save = useMutation({
    mutationFn: (s: RouteSettings) => api.saveRouteSettings({ start_type: s.start_type, label: s.label, street: s.street, postal_code: s.postal_code, city: s.city }),
    onSuccess: (data) => {
      queryClient.setQueryData(['me', 'route-settings'], data);
      setDraft(undefined);
    },
  });
  const value = draft ?? settings.data;
  return (
    <section className="card" aria-labelledby="route-title">
      <h2 id="route-title">Startpunkt der Tagesroute</h2>
      <p className="muted">Die Adresse wird nur für Ihre eigene Routenberechnung verwendet und anderen nicht angezeigt.</p>
      {settings.isError ? (
        <ErrorMessage error={settings.error} />
      ) : !value ? (
        <PageLoader />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(value);
          }}
        >
          <div className="form-grid">
            <label className="field">
              <span>Start</span>
              <select value={value.start_type} onChange={(e) => setDraft({ ...value, start_type: e.target.value as RouteSettings['start_type'] })}>
                {(Object.keys(startLabels) as RouteSettings['start_type'][]).map((k) => (
                  <option key={k} value={k}>
                    {startLabels[k]}
                  </option>
                ))}
              </select>
            </label>
            {(value.start_type === 'HOME' || value.start_type === 'CUSTOM') && (
              <>
                <label className="field">
                  <span>Straße (optional)</span>
                  <input value={value.street ?? ''} onChange={(e) => setDraft({ ...value, street: e.target.value })} autoComplete="street-address" />
                </label>
                <label className="field narrow">
                  <span>PLZ</span>
                  <input value={value.postal_code ?? ''} onChange={(e) => setDraft({ ...value, postal_code: e.target.value })} inputMode="numeric" autoComplete="postal-code" />
                </label>
                <label className="field">
                  <span>Ort</span>
                  <input value={value.city ?? ''} onChange={(e) => setDraft({ ...value, city: e.target.value })} autoComplete="address-level2" />
                </label>
              </>
            )}
          </div>
          {save.isError && <MutationError error={save.error} />}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={save.isPending || !draft}>
              Speichern
            </button>
            {save.isSuccess && <span className="muted">Gespeichert</span>}
          </div>
        </form>
      )}
    </section>
  );
}

// PasswordChange: Ändern beendet alle Sitzungen – danach erneut anmelden.
export function PasswordChange() {
  const { logout } = useAuth();
  const [form, setForm] = useState({ current: '', next: '', repeat: '' });
  const change = useMutation({ mutationFn: () => api.changePassword(form.current, form.next), onSuccess: () => void logout() });
  const mismatch = form.repeat !== '' && form.next !== form.repeat;
  return (
    <section className="card" aria-labelledby="password-title">
      <h2 id="password-title">Passwort ändern</h2>
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          if (!mismatch) change.mutate();
        }}
      >
        <label className="field">
          <span>Bisheriges Passwort</span>
          <input type="password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} autoComplete="current-password" required />
        </label>
        <label className="field">
          <span>Neues Passwort (mind. 10 Zeichen)</span>
          <input type="password" value={form.next} minLength={10} onChange={(e) => setForm({ ...form, next: e.target.value })} autoComplete="new-password" required />
        </label>
        <label className="field">
          <span>Wiederholen</span>
          <input type="password" value={form.repeat} onChange={(e) => setForm({ ...form, repeat: e.target.value })} autoComplete="new-password" required aria-invalid={mismatch} />
        </label>
        {mismatch && <p className="field-error">Die Passwörter stimmen nicht überein.</p>}
        {change.isError && <MutationError error={change.error} />}
        <div className="form-actions">
          <button type="submit" className="btn" disabled={change.isPending || mismatch}>
            Passwort ändern
          </button>
        </div>
      </form>
    </section>
  );
}
